import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next } from '@ember/runloop';
import { service } from '@ember/service';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { isAlive, PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';
import { cleanupDuplicateNodes } from 'webapp/utils/html';

import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { Args } from 'webapp/components/boards/index/base';
import BoardsIndexBaseComponent from 'webapp/components/boards/index/base';
import BoardsIndexGridItemComponent from 'webapp/components/boards/index/grid-item';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonAutoCreateOrCompleteBoardButtonComponent from 'webapp/components/common/auto-create-or-complete-board-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import CommonNewItemOptionsComponent from 'webapp/components/common/new-item-options';
import type { NewItemOption } from 'webapp/components/common/new-item-options';
import CommonRecentlyDeletedInfoComponent from 'webapp/components/common/recently-deleted-info';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import SkeletonScreenComponent from 'webapp/components/skeleton-screen';
import TemplatesTransferedNotificationAccountGridItemComponent from 'webapp/components/templates/transfered-notification/account/grid-item';
import TemplatesTransferedNotificationPublicGridItemComponent from 'webapp/components/templates/transfered-notification/public/grid-item';

import type { Tour } from 'webapp/models-custom';
import FilterSortBoardInGrid from 'webapp/models-custom/filter-sort/board-in-grid';

export default class BoardsIndexGridComponent extends BoardsIndexBaseComponent<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('tour') declare tourService: Service.Tour;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	override filterSort: FilterSortBoardInGrid;

	newDataboardOptionTop: NewItemOption = {
		action: this.createBoardWizard,
		icon: 'Wizard',
		text: 'Use wizard',
	};

	newDataboardOptionBottom: NewItemOption = {
		action: this.createBoard,
		icon: 'PencilRuler',
		text: 'Start blank ',
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortBoardInGrid({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.args.model;
			},
			onOpenView: (viewId: string) => {
				this.args.onOpenView(viewId);
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}

				this.args.updateIsRecentlyDeletedFilterActive(filterSort.isRecentlyDeletedSelected);
			},
		});

		this.filterSort = filterSort;
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'li.filter-sort-item[data-id]',
			shouldKeepInSection: (itemId, sectionSelector) => {
				const board = this.args.model.find((b) => b.id === itemId);
				if (!board) {
					return false;
				}
				return (
					(board.favorite && sectionSelector === '.favorites-section') ||
					(!board.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}

	get hasFavoriteBoards(): boolean {
		return this.args.model.filter((b) => b.favorite).length > 0;
	}

	setupBubbleTours() {
		const firstBoard = $('#home-boards li.card--board').eq(0);

		let tour1: Tour | null = null;

		// "first-board" tours
		next(this, () => {
			if (
				this.tourService.isEligibleForFirstBoardViewTour &&
				firstBoard.length &&
				!this.popupService.anyPopupsOpen
			) {
				tour1 = this.tourService.initTour(TourId.FIRST_BOARD_VIEW, { el: firstBoard });
				if (tour1) {
					tour1.onExplicitClose = () => {
						if (tour1) {
							tour1.end();
							tour1 = null;
						}

						firstBoard.addClass('card--show-overlay');
						firstBoard.one('mouseleave', () => {
							firstBoard.removeClass('card--show-overlay');
						});
					};
					tour1.start();
				}
			}
		});
	}

	@action
	allowAddBoardOrOpenLimitationPopup(): boolean {
		if (this.flowTourOnboardingService.isBannerShown) {
			this.flowTourOnboardingService.continueTour();
			return true;
		}

		return this.billingService.checkIfCan(BillingFeature.BOARDS, true);
	}

	@action
	setupView(node: HTMLElement, sortingFavorites: boolean): void {
		this.visibleCheckContainerNode = node;

		const itemSelectorString = this.filterSort.itemDomSelector,
			placeholderElementFn = (/*currentItem*/) => {
				return $(
					'<div class="drop-placeholder flex-item"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></div>',
				);
			};
		this.initSortable($(node), itemSelectorString, placeholderElementFn, sortingFavorites);

		// only setup bubble tours for the non-favorites section since that's what users see first
		if (!sortingFavorites && !this.currentUserService.isViewer) {
			later(() => {
				if (!isAlive(this)) {
					return;
				}

				if (this.popupService.anyPopupsOpen) {
					this.eventManagerService.on(AppEvent.ALL_POPUPS_CLOSED, this, () => {
						this.setupBubbleTours();
						return true; // just once, unbind after
					});
				} else {
					this.setupBubbleTours();
				}
			}, 1000);
		}
	}

	@action
	setupFavoritesView(node: HTMLElement): void {
		this.setupView(node, true);
	}

	@action
	setupNonFavoritesView(node: HTMLElement): void {
		this.setupView(node, false);
	}

	@action
	teardownView(): void {
		this.destroySortable();
	}

	@action
	cleanupDuplicateNodes(): void {
		cleanupDuplicateNodes(this.duplicateCleanupConfig);
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.ALL_POPUPS_CLOSED, this);
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} @entityName="dashboards" />

		{{#if this.isRecentlyDeletedActiveWithResults}}
			<CommonRecentlyDeletedInfoComponent
				@onDeleteAllPermanently={{this.deleteAllPermanently}}
			/>
		{{/if}}

		{{#if this.isRecentlyDeletedLoading}}
			<SkeletonScreenComponent @type="tiles" @item="board" />
		{{/if}}

		{{! Notifications boards section }}
		{{#if this.isNotificationsSectionVisible}}
			{{#unless this.isRecentlyDeletedActive}}
				<ul class="flex-ct flex-ct--size-board notifications-section">
					{{#each @transferedBoardAccountTemplateNotifications as |notification|}}
						<TemplatesTransferedNotificationAccountGridItemComponent
							@model={{notification}}
						/>
					{{/each}}

					{{#each @transferedBoardPublicTemplateNotifications as |notification|}}
						<TemplatesTransferedNotificationPublicGridItemComponent
							@model={{notification}}
						/>
					{{/each}}

					{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
					{{#each (makeArray length=21)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}
				</ul>
			{{/unless}}
		{{/if}}

		{{! Favorites section }}
		{{#if this.hasFavoriteBoards}}
			<CommonListLazyLoadComponent
				@classNames="lazy-grid"
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.favoriteBoardsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{300}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<ul
					class="flex-ct flex-ct--size-board favorites-section"
					data-hidden={{if this.noResults "true" "false"}}
					{{didInsert this.setupFavoritesView}}
					{{visibleCheckContainer}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{willDestroy this.teardownView}}
				>
					{{#each visibleItems as |item|}}
						<BoardsIndexGridItemComponent
							@model={{item}}
							@batchSelect={{this.batchSelect}}
							@hideSort={{exporter.hideSort}}
							@onCopy={{@onCopy}}
							@onOpenPreview={{this.openPreview}}
						/>
					{{/each}}

					{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
					{{#each (makeArray length=exporter.itemsPerRow)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}
				</ul>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>
		{{/if}}

		{{! Non-favorites section }}
		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@filterExtraFn={{this.nonFavoriteBoardsFilterFn}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{300}}
			@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
			as |visibleItems exporter|
		>
			<ul
				class="flex-ct flex-ct--size-board non-favorites-section"
				data-hidden={{if this.noResults "true" "false"}}
				{{didInsert this.setupNonFavoritesView}}
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |item|}}
					<BoardsIndexGridItemComponent
						@model={{item}}
						@batchSelect={{this.batchSelect}}
						@hideSort={{exporter.hideSort}}
						@onCopy={{@onCopy}}
						@onOpenPreview={{this.openPreview}}
						@onPermanentDelete={{this.handleSingleItemPermanentDelete}}
						@onRestore={{this.handleSingleItemRestore}}
					/>
				{{/each}}

				{{#if this.addBtnShown}}
					<CommonNewItemOptionsComponent
						@customOpenAction={{this.allowAddBoardOrOpenLimitationPopup}}
						@buttonText="New dashboard"
						@top={{this.newDataboardOptionTop}}
						@bottom={{this.newDataboardOptionBottom}}
					/>
				{{/if}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}

			</ul>

			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}
		</CommonListLazyLoadComponent>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{@onBoardCreateChoice}}
					@options={{this.addButtonOptions}}
					@onOptionClick={{this.doAddButtonAction}}
				/>
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}

		{{#if this.generateWithAIButtonContainerNode}}
			{{#in-element this.generateWithAIButtonContainerNode}}
				<CommonAutoCreateOrCompleteBoardButtonComponent
					@entryPoint={{this.createEntryPoint}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
